/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import api from 'api';
import AlertMessage, { AlertObj } from 'components/common/alert';
import { useState, useEffect } from 'react';
import { SvgIcon } from '@mui/material';
import { BiTrash } from 'react-icons/bi';
import { UnreadDot } from 'views/Dashboards-B2B/Dashboard.styled';
import { Notification } from 'api/models/response/Notifications';
import NotificationPopover from 'views/Dashboards-B2B/components/NotificationPopover';
import * as S from '../../../Dropdowns.styled';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';

const dateOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: '2-digit',

    hour: '2-digit',
    minute: '2-digit',
};

type NotificationsDropdownProps = {
    open: boolean;
    handleOpenCloseNotificationsDropdown: () => void;
    mobile: boolean;
};

const NotificationsDropdown = ({
    open,
    handleOpenCloseNotificationsDropdown,
    mobile,
}: NotificationsDropdownProps) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [clickedId, setClickedId] = useState<number | undefined>(undefined);
    const [anchorPosition, setAnchorPosition] = useState<{ left: number; top: number } | undefined>(
        undefined
    );
    const [isShowingAlert, setShowingAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState<AlertObj>({ type: 'info', value: '' });
    const [isUnreadNotifications, setIsUnreadNotifications] = useState(false);
    const [fetchNotifications, setFetchNotifications] = useState(false);

    useEffect(() => {
        if (open) {
            setIsUnreadNotifications(false);
        }
        if (notifications.length) {
            localStorage.setItem('clearAllNotificationsButton', open ? 'show' : 'hide');
            dispatchEvent(new Event('storageValueChanged'));
        } else {
            localStorage.setItem('clearAllNotificationsButton', 'hide');
            dispatchEvent(new Event('storageValueChanged'));
        }
    }, [open, notifications]);

    useEffect(() => {
        const handleStorageChange = () => {
            const reloadNotifications = localStorage.getItem('reloadNotifications');
            setFetchNotifications(reloadNotifications === 'yes');
        };

        window.addEventListener('storageValueChanged', handleStorageChange);
        return () => {
            window.removeEventListener('storageValueChanged', handleStorageChange);
        };
    }, []);

    useEffect((): void => {
        const fetchIsUnread = async () => {
            try {
                const response = await api.notification.getUnread();
                if (response.status !== 200) {
                    return false;
                }
                setIsUnreadNotifications(response.data['is_unread'] || false);
            } catch (e) {
                console.error(e);
            }
        };
        fetchIsUnread();
    }, []);

    const fetchData = async () => {
        try {
            const response = await api.notification.get();
            setNotifications(response.data);
        } catch (e: any) {
            console.error(e);
            if (e.response.status !== 200) {
                setAlertMessage({
                    type: 'error',
                    value: t('somethingWentWrong', { lng: language }),
                });
                setShowingAlert(true);
                return false;
            }
        }
    };

    useEffect(() => {
        if (open) {
            fetchData();
            if (fetchNotifications) {
                localStorage.setItem('reloadNotifications', 'no');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, fetchNotifications]);

    const deleteNotification = async (notification: Notification) => {
        try {
            await api.notification.deleteNotification(notification.id);
            fetchData();
        } catch (e: any) {
            console.error(e);
            if (e.response.status !== 200 || e.response.status !== 202) {
                setAlertMessage({
                    type: 'error',
                    value: t('somethingWentWrong', { lng: language }),
                });
                setShowingAlert(true);
                return false;
            }
        }
    };

    const deleteAllNotifications = () => {
        notifications.forEach((notification) => {
            deleteNotification(notification);
        });
    };

    const Alert = (
        <AlertMessage
            type={alertMessage?.type}
            text={alertMessage?.value}
            open={isShowingAlert}
            onClose={() => setShowingAlert(false)}
        ></AlertMessage>
    );

    const handleClick = (event: React.MouseEvent<HTMLElement>, notification: Notification) => {
        const target = event.target as HTMLElement;

        if (!target.closest('.trash-container')) {
            // if clicked anywhere except the trash icon
            if (anchorPosition) {
                setAnchorPosition(undefined);
            } else {
                setAnchorPosition({
                    left: event.clientX,
                    top: event.clientY,
                });
            }
            if (clickedId) {
                setClickedId(undefined);
            } else {
                setClickedId(notification.id);
            }
        } else if (target.closest('.trash-container')) {
            deleteNotification(notification);
        }
    };

    const { t } = useTranslation();

    return (
        <>
            {Alert}
            <S.Dropdown onClick={() => handleOpenCloseNotificationsDropdown()}>
                <span style={{ display: 'flex', alignItems: 'flex-start' }}>
                    {t('notifications', { lng: language })}
                    {isUnreadNotifications && (
                        <UnreadDot style={{ marginTop: '4px', marginLeft: '7px' }} />
                    )}
                </span>

                <S.DropdownIcon style={{ width: '17px', transform: open ? 'scaleY(-1)' : '' }} />
            </S.Dropdown>

            <S.NotificationsContainer mobile={mobile} open={open}>
                {!mobile && notifications.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <S.ClearAllButton onClick={deleteAllNotifications}>
                            {t('clearAllNotifications', { lng: language })}
                        </S.ClearAllButton>
                    </div>
                )}

                {notifications.length > 0 &&
                    notifications.map((notification: Notification) => {
                        return (
                            <S.NotificationContainer
                                onClick={(e) => handleClick(e, notification)}
                                key={notification.id}
                            >
                                <NotificationPopover
                                    anchorPosition={anchorPosition}
                                    notification={notification}
                                    clickedId={clickedId}
                                />
                                <S.TopContainer>
                                    <S.Message>{notification.message}</S.Message>

                                    <S.TrashContainer className="trash-container">
                                        <SvgIcon
                                            style={{
                                                width: '15',
                                                height: '16.7px',
                                            }}
                                            viewBox="3 5 18 16.7"
                                            component={BiTrash}
                                        />
                                    </S.TrashContainer>
                                </S.TopContainer>
                                <S.BottomContainer>
                                    {notification.created_at && (
                                        <S.CreatedAt>
                                            {new Date(notification.created_at).toLocaleString(
                                                language === 'EN' ? 'en-US' : 'fr-FR',
                                                dateOptions
                                            )}
                                        </S.CreatedAt>
                                    )}
                                </S.BottomContainer>
                            </S.NotificationContainer>
                        );
                    })}
                {notifications && notifications.length === 0 && (
                    <S.NotificationContainer>
                        <S.TopContainer>
                            <S.Message> {t('noNotifications', { lng: language })}</S.Message>
                        </S.TopContainer>
                    </S.NotificationContainer>
                )}
            </S.NotificationsContainer>
        </>
    );
};

export default NotificationsDropdown;
